import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import TenantLayout from '../components/TenantLayout';
import { useSite } from '../context/SiteContext';
import QRCode from 'qrcode';

const TenantSites = () => {
  const { selectedSite: contextSelectedSite, updateTrigger } = useSite();
  const [sites, setSites] = useState([]);
  const [filteredSites, setFilteredSites] = useState([]);
  const [selectedCity, setSelectedCity] = useState('all');
  const [availableCities, setAvailableCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: 'Odisha',
    category: 'other',
    coordinates: {
      latitude: '',
      longitude: ''
    },
    features: [],
    description: '',
    childAgeLimit: 12,
    operatingHours: {
      openTime: '06:00',
      closeTime: '20:00'
    },
    contact: {
      phone: '',
      email: ''
    }
  });
  const [uploadedImages, setUploadedImages] = useState([]);
  const [showQRModal, setShowQRModal] = useState(false);
  const [selectedSiteForQR, setSelectedSiteForQR] = useState(null);
  const [qrCodeDataURL, setQrCodeDataURL] = useState('');

  const allAvailableFeatures = [
    { key: 'entry_ticket', label: 'Entry Ticket', icon: '🎫' },
    { key: 'parking_ticket', label: 'Parking', icon: '🅿️' },
    { key: 'chappal_stand_ticket', label: 'Chappal Stand', icon: '👟' },
    { key: 'puja_ticket', label: 'Puja Services', icon: '🙏' },
    { key: 'event_ticket', label: 'Events', icon: '🎭' },
    { key: 'photo_shoot_ticket', label: 'Photo Shoots', icon: '📸' },
    { key: 'function_hall_ticket', label: 'Function Hall', icon: '🎉' },
    { key: 'pass_ticket', label: 'Pass Tickets', icon: '🎟️' },
    { key: 'other', label: 'Other', icon: '🏛️' }
  ];

  // Filter features based on tenant's allowed features
  const [tenantAllowedFeatures, setTenantAllowedFeatures] = useState([]);
  const [featuresLoading, setFeaturesLoading] = useState(true);
  
  useEffect(() => {
    const fetchTenantFeatures = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_BASE_URL}/api/tenants`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const currentTenant = response.data.tenants.find(t => t._id === user.tenantId);
        const allowedFeatures = currentTenant?.allowedFeatures || allAvailableFeatures.map(f => f.key);
        setTenantAllowedFeatures(allowedFeatures);
      } catch (error) {
        console.error('Error fetching tenant features:', error);
        setTenantAllowedFeatures(allAvailableFeatures.map(f => f.key));
      } finally {
        setFeaturesLoading(false);
      }
    };
    fetchTenantFeatures();
  }, []);
  
  const availableFeatures = featuresLoading 
    ? [] 
    : allAvailableFeatures.filter(feature => tenantAllowedFeatures.includes(feature.key));
  
  console.log('Features loading:', featuresLoading);
  console.log('Tenant allowed features:', tenantAllowedFeatures);
  console.log('Available features after filtering:', availableFeatures);

  const siteCategories = [
    { key: 'temple', label: 'Temple', icon: '🛕' },
    { key: 'beach', label: 'Beach', icon: '🏖️' },
    { key: 'park', label: 'Park', icon: '🌳' },
    { key: 'historical', label: 'Historical Place', icon: '🏰' },
    { key: 'mountain', label: 'Mountain', icon: '⛰️' },
    { key: 'waterfall', label: 'Waterfall', icon: '💧' },
    { key: 'lake', label: 'Lake', icon: '🏞️' },
    { key: 'cave', label: 'Cave', icon: '🕳️' },
    { key: 'dam', label: 'Dam', icon: '🌊' },
    { key: 'wildlife', label: 'Wildlife Sanctuary', icon: '🦁' },
    { key: 'museum', label: 'Museum', icon: '🏛️' },
    { key: 'other', label: 'Other', icon: '📍' }
  ];

  useEffect(() => {
    fetchSites();
  }, [updateTrigger]);
  
  useEffect(() => {
    console.log('TenantSites filtering - contextSelectedSite:', contextSelectedSite, 'sites:', sites.length);
    // Apply site filter from context
    if (contextSelectedSite && contextSelectedSite !== 'all') {
      const filteredBySite = sites.filter(site => site._id === contextSelectedSite);
      console.log('Filtered by site:', filteredBySite.length, 'sites');
      setFilteredSites(filteredBySite);
    } else {
      // Apply city filter
      if (selectedCity === 'all') {
        setFilteredSites(sites);
      } else {
        setFilteredSites(sites.filter(site => site.city === selectedCity));
      }
    }
  }, [sites, selectedCity, contextSelectedSite]);

  const fetchSites = async () => {
    try {
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      const response = await axios.get(`${API_BASE_URL}/api/sites`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'x-tenant-id': user.tenantId
        }
      });
      const sitesData = response.data.sites || [];
      setSites(sitesData);
      
      // Extract unique cities
      const cities = [...new Set(sitesData.map(site => site.city))].sort();
      setAvailableCities(cities);
      
      // Apply current city filter
      if (selectedCity === 'all') {
        setFilteredSites(sitesData);
      } else {
        setFilteredSites(sitesData.filter(site => site.city === selectedCity));
      }
    } catch (error) {
      console.error('Error fetching sites:', error);
      setSites([]);
      setFilteredSites([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const siteData = {
        ...formData,
        coordinates: {
          latitude: parseFloat(formData.coordinates.latitude),
          longitude: parseFloat(formData.coordinates.longitude)
        }
      };
      
      if (editingSite) {
        await axios.put(`${API_BASE_URL}/api/sites/${editingSite._id}`, siteData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'x-tenant-id': user.tenantId
          }
        });
      } else {
        await axios.post(`${API_BASE_URL}/api/sites`, siteData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'x-tenant-id': user.tenantId
          }
        });
      }
      fetchSites();
      resetForm();
    } catch (error) {
      alert('Error saving site: ' + (error.response?.data?.message || error.message));
    }
  };

  const startEdit = (site) => {
    setFormData({
      name: site.name,
      address: site.address || '',
      city: site.city,
      state: site.state || 'Odisha',
      category: site.category || 'other',
      coordinates: {
        latitude: site.coordinates?.latitude || '',
        longitude: site.coordinates?.longitude || ''
      },
      features: site.features || [],
      description: site.description || '',
      childAgeLimit: site.childAgeLimit || 12,
      operatingHours: {
        openTime: site.operatingHours?.openTime || '06:00',
        closeTime: site.operatingHours?.closeTime || '20:00'
      },
      contact: {
        phone: site.contact?.phone || '',
        email: site.contact?.email || ''
      }
    });
    setEditingSite(site);
    setUploadedImages(site.images || []);
    setShowAddForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this site?')) {
      try {
        const token = localStorage.getItem('token');
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        await axios.delete(`${API_BASE_URL}/api/sites/${id}`, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'x-tenant-id': user.tenantId
          }
        });
        fetchSites();
      } catch (error) {
        alert('Error deleting site: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      address: '',
      city: '',
      state: 'Odisha',
      category: 'other',
      coordinates: { latitude: '', longitude: '' },
      features: [],
      description: '',
      childAgeLimit: 12,
      operatingHours: { openTime: '06:00', closeTime: '20:00' },
      contact: { phone: '', email: '' }
    });
    setShowAddForm(false);
    setEditingSite(null);
  };

  const toggleFeature = (feature) => {
    const updatedFeatures = formData.features.includes(feature)
      ? formData.features.filter(f => f !== feature)
      : [...formData.features, feature];
    setFormData({ ...formData, features: updatedFeatures });
  };

  const handleToggleAllSites = async () => {
    const activeSites = sites.filter(site => site.isActive !== false);
    const isDisabling = activeSites.length > 0;
    const action = isDisabling ? 'disable' : 'enable';
    
    if (window.confirm(`Are you sure you want to ${action} all sites?`)) {
      try {
        const token = localStorage.getItem('token');
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        await axios.put(`${API_BASE_URL}/api/sites/toggle-all`, {}, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'x-tenant-id': user.tenantId
          }
        });
        fetchSites();
        alert(`All sites have been ${action}d successfully.`);
      } catch (error) {
        alert(`Error ${action}ing sites: ` + (error.response?.data?.message || error.message));
      }
    }
  };

  const handleToggleSite = async (siteId, currentStatus) => {
    const newStatus = !currentStatus;
    const action = newStatus ? 'enable' : 'disable';
    
    try {
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      await axios.put(`${API_BASE_URL}/api/sites/${siteId}`, { isActive: newStatus }, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'x-tenant-id': user.tenantId
        }
      });
      fetchSites();
    } catch (error) {
      alert(`Error ${action}ing site: ` + (error.response?.data?.message || error.message));
    }
  };

  const handleImageUpload = async (siteId, files) => {
    if (!files || files.length === 0) return;
    
    const formData = new FormData();
    Array.from(files).forEach(file => {
      formData.append('images', file);
    });
    
    try {
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await axios.post(`${API_BASE_URL}/api/sites/${siteId}/images`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'x-tenant-id': user.tenantId,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Update local state immediately
      const updatedSite = sites.find(s => s._id === siteId);
      if (updatedSite && response.data.images) {
        setUploadedImages([...uploadedImages, ...response.data.images]);
        updatedSite.images = [...(updatedSite.images || []), ...response.data.images];
        setEditingSite(updatedSite);
      }
      
      fetchSites();
    } catch (error) {
      alert('Error uploading images: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDeleteImage = async (siteId, imageId) => {
    if (!window.confirm('Delete this image?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      await axios.delete(`${API_BASE_URL}/api/sites/${siteId}/images/${imageId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'x-tenant-id': user.tenantId
        }
      });
      fetchSites();
    } catch (error) {
      alert('Error deleting image: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleCityFilter = (city) => {
    setSelectedCity(city);
    if (city === 'all') {
      setFilteredSites(sites);
    } else {
      setFilteredSites(sites.filter(site => site.city === city));
    }
  };

  const generateSiteURL = (site) => {
    const baseURL = window.location.origin;
    return `${baseURL}/site/${site._id}`;
  };

  const handleGenerateQR = async (site) => {
    try {
      const siteURL = generateSiteURL(site);
      const qrDataURL = await QRCode.toDataURL(siteURL, {
        width: 300,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      setQrCodeDataURL(qrDataURL);
      setSelectedSiteForQR(site);
      setShowQRModal(true);
    } catch (error) {
      console.error('Error generating QR code:', error);
      alert('Error generating QR code');
    }
  };

  const downloadQRCode = () => {
    const link = document.createElement('a');
    link.download = `${selectedSiteForQR.name}-QR-Code.png`;
    link.href = qrCodeDataURL;
    link.click();
  };

  if (loading) {
    return (
      <TenantLayout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
          <div style={{ fontSize: '18px' }}>Loading sites...</div>
        </div>
      </TenantLayout>
    );
  }

  return (
    <TenantLayout>
      <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
          <div>
            <h1 style={{ margin: '0 0 10px 0', color: '#2d3748', fontSize: '28px' }}>Sites Management</h1>
            <p style={{ color: '#718096', margin: 0 }}>Manage your sites and ticket types</p>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <select
              value={selectedCity}
              onChange={(e) => handleCityFilter(e.target.value)}
              style={{
                padding: '12px 16px',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '14px',
                background: 'white',
                cursor: 'pointer'
              }}
            >
              <option value="all">All Cities ({sites.length})</option>
              {availableCities.map(city => (
                <option key={city} value={city}>
                  {city} ({sites.filter(site => site.city === city).length})
                </option>
              ))}
            </select>
            <button
              onClick={handleToggleAllSites}
              style={{
                background: sites.filter(site => site.isActive !== false).length > 0 ? '#e53e3e' : 'linear-gradient(135deg, #06b6d4, #10b981)',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: sites.filter(site => site.isActive !== false).length > 0 ? '0 4px 15px rgba(229, 62, 62, 0.3)' : '0 4px 15px rgba(6, 182, 212, 0.3)'
              }}
            >
              {sites.filter(site => site.isActive !== false).length > 0 ? '🚫 Disable All Sites' : '✅ Enable All Sites'}
            </button>
            <button
              onClick={() => setShowAddForm(true)}
              style={{
                background: 'linear-gradient(135deg, #06b6d4, #10b981)',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 4px 15px rgba(6, 182, 212, 0.3)'
              }}
            >
              + Add New Site
            </button>
          </div>
        </div>

        {showAddForm && (
          <div style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)',
            padding: '32px',
            borderRadius: '16px',
            boxShadow: '0 20px 60px rgba(6, 182, 212, 0.15)',
            border: '1px solid rgba(6, 182, 212, 0.1)',
            marginBottom: '32px',
            backdropFilter: 'blur(10px)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                background: 'linear-gradient(135deg, #06b6d4, #10b981)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 25px rgba(6, 182, 212, 0.3)'
              }}>
                <span style={{ color: 'white', fontSize: '1.25rem' }}>{editingSite ? '✏️' : '➕'}</span>
              </div>
              <h3 style={{ fontSize: '2rem', fontWeight: '700', color: '#1e293b' }}>
                {editingSite ? 'Edit Site' : 'Add New Site'}
              </h3>
            </div>
            
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : 'repeat(3, 1fr)', gap: '16px' }}>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Site Name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white/80 backdrop-blur-sm"
                  />
                  <span className="absolute top-4 right-4 text-red-500">*</span>
                </div>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="City"
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                    required
                    className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white/80 backdrop-blur-sm"
                  />
                  <span className="absolute top-4 right-4 text-red-500">*</span>
                </div>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white/80 backdrop-blur-sm cursor-pointer"
                >
                  {siteCategories.map(category => (
                    <option key={category.key} value={category.key}>
                      {category.icon} {category.label}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="relative">
                <textarea
                  placeholder="Address"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  required
                  rows="2"
                  className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white/80 backdrop-blur-sm resize-none"
                />
                <span className="absolute top-4 right-4 text-red-500">*</span>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="relative">
                  <input
                    type="number"
                    step="any"
                    placeholder="Latitude"
                    value={formData.coordinates.latitude}
                    onChange={(e) => setFormData({
                      ...formData,
                      coordinates: { ...formData.coordinates, latitude: e.target.value }
                    })}
                    required
                    className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white/80 backdrop-blur-sm"
                  />
                  <span className="absolute top-4 right-4 text-red-500">*</span>
                </div>
                <div className="relative">
                  <input
                    type="number"
                    step="any"
                    placeholder="Longitude"
                    value={formData.coordinates.longitude}
                    onChange={(e) => setFormData({
                      ...formData,
                      coordinates: { ...formData.coordinates, longitude: e.target.value }
                    })}
                    required
                    className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white/80 backdrop-blur-sm"
                  />
                  <span className="absolute top-4 right-4 text-red-500">*</span>
                </div>
              </div>
              
              <div>
                <label className="block text-lg font-semibold text-gray-700 mb-3">Available Features</label>
                <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth <= 768 ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', gap: '12px' }}>
                  {availableFeatures.map(feature => (
                    <label key={feature.key} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '16px',
                      border: formData.features.includes(feature.key) ? '2px solid #06b6d4' : '2px solid #e2e8f0',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      background: formData.features.includes(feature.key) ? 'rgba(6, 182, 212, 0.05)' : 'rgba(255,255,255,0.9)',
                      boxShadow: formData.features.includes(feature.key) ? '0 4px 15px rgba(6, 182, 212, 0.1)' : 'none'
                    }}
                    onMouseOver={(e) => {
                      if (!formData.features.includes(feature.key)) {
                        e.currentTarget.style.borderColor = '#cbd5e1';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (!formData.features.includes(feature.key)) {
                        e.currentTarget.style.borderColor = '#e2e8f0';
                        e.currentTarget.style.boxShadow = 'none';
                      }
                    }}>
                      <input
                        type="checkbox"
                        checked={formData.features.includes(feature.key)}
                        onChange={() => toggleFeature(feature.key)}
                        style={{
                          width: '20px',
                          height: '20px',
                          accentColor: '#06b6d4',
                          borderRadius: '4px'
                        }}
                      />
                      <span style={{ fontSize: '1.25rem' }}>{feature.icon}</span>
                      <span style={{ fontWeight: '500', color: '#374151' }}>{feature.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <textarea
                placeholder="Description (optional)"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows="3"
                className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white/80 backdrop-blur-sm resize-none"
              />
              
              <div>
                <label className="block text-lg font-semibold text-gray-700 mb-2">Child Age Limit</label>
                <div className="flex items-center gap-4">
                  <input
                    type="number"
                    min="0"
                    max="18"
                    value={formData.childAgeLimit}
                    onChange={(e) => setFormData({...formData, childAgeLimit: parseInt(e.target.value) || 12})}
                    className="w-32 p-4 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white/80 backdrop-blur-sm"
                  />
                  <span className="text-gray-600">years</span>
                </div>
                <p className="text-sm text-gray-500 mt-2">Children below this age get child pricing</p>
              </div>
              
              {editingSite && (
                <div className="bg-white/60 backdrop-blur-sm p-6 rounded-xl border-2 border-dashed border-gray-300">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
                      📸 Site Images ({editingSite.images?.length || 0})
                    </h4>
                    <label style={{
                      background: 'linear-gradient(135deg, #06b6d4, #10b981)',
                      color: 'white',
                      padding: '8px 16px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      boxShadow: '0 4px 15px rgba(6, 182, 212, 0.3)',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.transform = 'translateY(-1px)';
                      e.target.style.boxShadow = '0 6px 20px rgba(6, 182, 212, 0.4)';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 4px 15px rgba(6, 182, 212, 0.3)';
                    }}>
                      <span>📁 Upload Images</span>
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={(e) => handleImageUpload(editingSite._id, e.target.files)}
                        className="hidden"
                      />
                    </label>
                  </div>
                  
                  {editingSite.images?.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                      {editingSite.images.map(image => (
                        <div key={image._id} className="relative group">
                          <img 
                            src={`https://tiketjini.com${image.path}`} 
                            alt={image.originalName}
                            className="w-full h-24 object-cover rounded-lg border-2 border-gray-200 group-hover:border-blue-500 transition-all duration-200"
                          />
                          <button
                            onClick={() => handleDeleteImage(editingSite._id, image._id)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600 transition-colors opacity-0 group-hover:opacity-100"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <span className="text-4xl mb-2 block">📷</span>
                      <p>No images uploaded yet</p>
                    </div>
                  )}
                </div>
              )}
              
              <div style={{ display: 'flex', gap: '16px', paddingTop: '16px' }}>
                <button
                  type="submit"
                  style={{
                    background: 'linear-gradient(135deg, #06b6d4, #10b981)',
                    color: 'white',
                    padding: '16px 32px',
                    borderRadius: '12px',
                    fontSize: '16px',
                    fontWeight: '600',
                    border: 'none',
                    cursor: 'pointer',
                    boxShadow: '0 8px 25px rgba(6, 182, 212, 0.3)',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 12px 35px rgba(6, 182, 212, 0.4)';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 8px 25px rgba(6, 182, 212, 0.3)';
                  }}
                >
                  <span>{editingSite ? '💾' : '➕'}</span>
                  {editingSite ? 'Update Site' : 'Create Site'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  style={{
                    background: '#e2e8f0',
                    color: '#64748b',
                    padding: '16px 32px',
                    borderRadius: '12px',
                    fontSize: '16px',
                    fontWeight: '600',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.background = '#cbd5e1';
                    e.target.style.color = '#475569';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.background = '#e2e8f0';
                    e.target.style.color = '#64748b';
                  }}
                >
                  <span>❌</span>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredSites.map(site => (
            <div key={site._id} style={{
              background: 'rgba(255,255,255,0.95)',
              backdropFilter: 'blur(10px)',
              borderRadius: '16px',
              boxShadow: '0 8px 25px rgba(6, 182, 212, 0.1)',
              border: '1px solid rgba(6, 182, 212, 0.1)',
              overflow: 'hidden',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 15px 40px rgba(6, 182, 212, 0.2)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 8px 25px rgba(6, 182, 212, 0.1)';
            }}>
              <div style={{ background: 'linear-gradient(135deg, #06b6d4, #10b981)', padding: '24px', color: 'white' }}>
                <div className="flex items-center justify-between mb-4">
                  <div style={{ width: '48px', height: '48px', background: 'rgba(255,255,255,0.2)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span className="text-2xl">
                      {siteCategories.find(cat => cat.key === site.category)?.icon || '🏛️'}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleGenerateQR(site)}
                      style={{
                        background: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        border: 'none',
                        padding: '4px 8px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        cursor: 'pointer'
                      }}
                      title="Generate QR Code"
                    >
                      📱
                    </button>
                    <button
                      onClick={() => handleToggleSite(site._id, site.isActive !== false)}
                      style={{
                        background: site.isActive !== false ? '#f56565' : '#10b981',
                        color: 'white',
                        border: 'none',
                        padding: '4px 8px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        cursor: 'pointer'
                      }}
                      title={site.isActive !== false ? 'Disable site' : 'Enable site'}
                    >
                      {site.isActive !== false ? '🚫' : '✅'}
                    </button>
                    <button
                      onClick={() => startEdit(site)}
                      style={{
                        background: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        border: 'none',
                        padding: '4px 12px',
                        borderRadius: '8px',
                        fontSize: '0.875rem',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseOver={(e) => e.target.style.background = 'rgba(255,255,255,0.3)'}
                      onMouseOut={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleDelete(site._id)}
                      style={{
                        background: 'rgba(239, 68, 68, 0.8)',
                        color: 'white',
                        border: 'none',
                        padding: '4px 12px',
                        borderRadius: '8px',
                        fontSize: '0.875rem',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseOver={(e) => e.target.style.background = '#dc2626'}
                      onMouseOut={(e) => e.target.style.background = 'rgba(239, 68, 68, 0.8)'}
                    >
                      🗑️
                    </button>
                  </div>
                </div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '8px', lineHeight: '1.2' }}>{site.name}</h3>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: 'rgba(255,255,255,0.9)' }}>
                  <span style={{ fontSize: '0.875rem' }}>📍 {site.city}</span>
                  <span style={{ fontSize: '0.75rem', background: 'rgba(255,255,255,0.2)', padding: '4px 8px', borderRadius: '20px' }}>
                    {siteCategories.find(cat => cat.key === site.category)?.label || 'Other'}
                  </span>
                </div>
              </div>
              
              <div className="p-6">
                <div className="mb-4">
                  <div className="flex flex-wrap gap-2 mb-3">
                    {site.features?.map(feature => {
                      const featureInfo = availableFeatures.find(f => f.key === feature);
                      return (
                        <span key={feature} className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                          <span>{featureInfo?.icon}</span>
                          {featureInfo?.label}
                        </span>
                      );
                    })}
                  </div>
                  <div className="text-sm text-gray-600">
                    📍 {site.address}
                  </div>
                </div>
                
                {site.description && (
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                    {site.description}
                  </p>
                )}
                
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <div className="text-sm text-gray-500">
                    🕒 {site.operatingHours?.openTime || '06:00'} - {site.operatingHours?.closeTime || '20:00'}
                  </div>
                  <div className="flex gap-2">
                    {site.coordinates?.latitude && site.coordinates?.longitude && (
                      <a
                        href={`https://www.google.com/maps?q=${site.coordinates.latitude},${site.coordinates.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          background: '#0891b2',
                          color: 'white',
                          padding: '8px 12px',
                          borderRadius: '8px',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          textDecoration: 'none',
                          transition: 'all 0.3s ease'
                        }}
                        title="View on Google Maps"
                        onMouseOver={(e) => e.target.style.background = '#0e7490'}
                        onMouseOut={(e) => e.target.style.background = '#0891b2'}
                      >
                        📍
                      </a>
                    )}
                    <Link
                      to={`/tenant-dashboard/tickets?site=${site._id}`}
                      style={{
                        background: 'linear-gradient(135deg, #06b6d4, #10b981)',
                        color: 'white',
                        padding: '8px 16px',
                        borderRadius: '8px',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        textDecoration: 'none',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 4px 15px rgba(6, 182, 212, 0.3)'
                      }}
                      onMouseOver={(e) => {
                        e.target.style.transform = 'translateY(-1px)';
                        e.target.style.boxShadow = '0 6px 20px rgba(6, 182, 212, 0.4)';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 15px rgba(6, 182, 212, 0.3)';
                      }}
                    >
                      Manage Tickets 🎫
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredSites.length === 0 && sites.length > 0 && (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            background: '#f7fafc',
            borderRadius: '12px',
            border: '2px dashed #cbd5e0'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>🔍</div>
            <h3 style={{ color: '#4a5568', marginBottom: '10px' }}>No Sites Found in {selectedCity}</h3>
            <p style={{ color: '#718096', marginBottom: '20px' }}>
              Try selecting a different city or add sites for this location
            </p>
            <button
              onClick={() => handleCityFilter('all')}
              style={{
                background: 'linear-gradient(135deg, #06b6d4, #10b981)',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 4px 15px rgba(6, 182, 212, 0.3)'
              }}
            >
              Show All Sites
            </button>
          </div>
        )}

        {sites.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            background: '#f7fafc',
            borderRadius: '12px',
            border: '2px dashed #cbd5e0'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>🏛️</div>
            <h3 style={{ color: '#4a5568', marginBottom: '10px' }}>No Sites Found</h3>
            <p style={{ color: '#718096', marginBottom: '20px' }}>
              Create your first site to start managing tickets and bookings
            </p>
            <button
              onClick={() => setShowAddForm(true)}
              style={{
                background: 'linear-gradient(135deg, #06b6d4, #10b981)',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 4px 15px rgba(6, 182, 212, 0.3)'
              }}
            >
              Add Your First Site
            </button>
          </div>
        )}

        {/* QR Code Modal */}
        {showQRModal && selectedSiteForQR && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">Site QR Code</h3>
                <button
                  onClick={() => setShowQRModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="text-2xl">×</span>
                </button>
              </div>
              
              <div className="text-center">
                <div className="mb-4">
                  <h4 className="text-lg font-semibold text-gray-800 mb-2">{selectedSiteForQR.name}</h4>
                  <p className="text-sm text-gray-600 mb-4">{selectedSiteForQR.city}</p>
                </div>
                
                <div className="bg-white p-4 rounded-lg border-2 border-gray-200 mb-4 inline-block">
                  <img src={qrCodeDataURL} alt="QR Code" className="w-64 h-64" />
                </div>
                
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">Scan to book tickets for this site</p>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 break-all">{generateSiteURL(selectedSiteForQR)}</p>
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowQRModal(false)}
                    className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
                  >
                    Close
                  </button>
                  <button
                    onClick={downloadQRCode}
                    className="flex-1 px-4 py-2 bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white rounded-lg font-medium transition-all shadow-lg"
                  >
                    💾 Download QR
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </TenantLayout>
  );
};

export default TenantSites;